import { useCallback, useState } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import { Plus, Upload, X } from "@/components/icons"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { LanguageSelect } from "@/features/base-settings/components/language-select"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { uiOption } from "@/lib/ui-options"
import {
  sanitizeResearchDepth,
  useResearchPreferencesStore,
} from "@/stores/research-preferences-store"

import { SourcesRequiredDialog } from "./sources-required-dialog"
import { UrlList } from "./url-list"
import { useResearchPreferencesActions } from "../hooks/use-research-preferences-actions"

const WORDS_PER_PAGE = 500
const MAX_PAGES = 50

const isDev = uiOption("show_dev_options")

type ResearchSettingsContentProps = {
  sessionType: string
}

export function ResearchSettingsContent({ sessionType }: ResearchSettingsContentProps) {
  const { t } = useTranslation()
  const webSearchEnabled = useWebSearchEnabled()

  const researchPrefsStore = useResearchPreferencesStore(sessionType)
  const researchPrefs = researchPrefsStore((state) => state.prefs)

  // Use custom hook to encapsulate store actions
  const { reportActions, sourceActions, languageActions, configActions } =
    useResearchPreferencesActions(sessionType)

  const [newUrl, setNewUrl] = useState("")
  const [showSourcesRequired, setShowSourcesRequired] = useState(false)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        reportActions.setReportFormatFile(acceptedFiles[0])
      }
    },
    [reportActions]
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    noClick: true,
  })

  const currentUrls = researchPrefs.sourceUrls
    ? researchPrefs.sourceUrls.split("\n").filter(Boolean)
    : []

  const addUrl = () => {
    const trimmedUrl = newUrl.trim()
    if (trimmedUrl && !currentUrls.includes(trimmedUrl)) {
      sourceActions.setSourceUrls([...currentUrls, trimmedUrl].join("\n"))
      setNewUrl("")
    }
  }

  const removeUrl = (index: number) => {
    const updatedUrls = currentUrls.filter((_, i) => i !== index)
    sourceActions.setSourceUrls(updatedUrls.join("\n"))
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  return (
    <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain">
      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.sources")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.sources_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <div className="flex flex-col gap-1 pl-2">
          <Label className="flex items-center gap-2" htmlFor="source-company">
            <Checkbox
              checked={researchPrefs.sources.includes("company_libraries")}
              id="source-company"
              onCheckedChange={(checked) => {
                const isChecked = Boolean(checked)
                const hasCompany = researchPrefs.sources.includes("company_libraries")

                if (!isChecked && hasCompany && researchPrefs.sources.length === 1) {
                  setShowSourcesRequired(true)
                  return
                }

                if (isChecked && !hasCompany) {
                  sourceActions.setSources([...researchPrefs.sources, "company_libraries"])
                } else if (!isChecked && hasCompany) {
                  sourceActions.setSources(
                    researchPrefs.sources.filter(
                      (source) => source !== "company_libraries"
                    ) as typeof researchPrefs.sources
                  )
                }
              }}
            />
            <span className="text-sm">{t("pages.ai_explorer.sources_company_libraries")}</span>
          </Label>
          {webSearchEnabled && (
            <Label className="flex items-center gap-2" htmlFor="source-web">
              <Checkbox
                checked={researchPrefs.sources.includes("web")}
                id="source-web"
                onCheckedChange={(checked) => {
                  const isChecked = Boolean(checked)
                  const sources = new Set(researchPrefs.sources)

                  if (!isChecked && sources.has("web") && sources.size === 1) {
                    setShowSourcesRequired(true)
                    return
                  }

                  if (isChecked) {
                    sources.add("web")
                  } else {
                    sources.delete("web")
                  }
                  sourceActions.setSources(Array.from(sources) as typeof researchPrefs.sources)
                }}
              />
              <span className="text-sm">{t("pages.ai_explorer.sources_web")}</span>
            </Label>
          )}
          <Label className="flex items-center gap-2" htmlFor="source-documents">
            <Checkbox
              checked={researchPrefs.sources.includes("user_documents")}
              id="source-documents"
              onCheckedChange={(checked) => {
                const isChecked = Boolean(checked)
                const sources = new Set(researchPrefs.sources)

                if (!isChecked && sources.has("user_documents") && sources.size === 1) {
                  setShowSourcesRequired(true)
                  return
                }

                if (isChecked) {
                  sources.add("user_documents")
                } else {
                  sources.delete("user_documents")
                }
                sourceActions.setSources(Array.from(sources) as typeof researchPrefs.sources)
              }}
            />
            <span className="text-sm">{t("pages.ai_explorer.sources_uploaded_files")}</span>
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label htmlFor="file-handling">{t("pages.ai_explorer.file_handling")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.file_handling_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select
          value={researchPrefs.fileHandlingStrategy}
          onValueChange={configActions.setFileHandlingStrategy}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("pages.ai_explorer.file_handling_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="use_files_as_context">
              {t("pages.ai_explorer.file_handling_use_files_as_context")}
            </SelectItem>
            <SelectItem value="search_in_files">
              {t("pages.ai_explorer.file_handling_search_in_files")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label htmlFor="report-length">{t("pages.ai_explorer.report_length")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_length_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select
          value={researchPrefs.reportLength}
          onValueChange={(val) => {
            reportActions.setReportLength(val as typeof researchPrefs.reportLength)
            if (val !== "custom") {
              reportActions.setCustomReportLength(2000)
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("pages.ai_explorer.report_length_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="short">{t("pages.ai_explorer.report_length_short")}</SelectItem>
            <SelectItem value="standard">
              {t("pages.ai_explorer.report_length_standard")}
            </SelectItem>
            <SelectItem value="extended">
              {t("pages.ai_explorer.report_length_extended")}
            </SelectItem>
            <SelectItem value="custom">{t("pages.ai_explorer.report_length_custom")}</SelectItem>
          </SelectContent>
        </Select>
        {researchPrefs.reportLength === "custom" && (
          <Input
            max={MAX_PAGES}
            min={1}
            placeholder={t("pages.ai_explorer.report_length_custom_placeholder")}
            step={1}
            type="number"
            value={Math.round(researchPrefs.customReportLength / WORDS_PER_PAGE)}
            onChange={(e) => {
              const pages = Math.min(Math.max(Number(e.target.value), 1), 60)
              reportActions.setCustomReportLength(pages * WORDS_PER_PAGE)
            }}
          />
        )}
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label htmlFor="research-depth">{t("pages.ai_explorer.research_depth")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.research_depth_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select
          value={sanitizeResearchDepth(researchPrefs.researchDepth)}
          onValueChange={configActions.setResearchDepth}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("pages.ai_explorer.research_depth_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {isDev && <SelectItem value="dev">dev (superquick)</SelectItem>}
            <SelectItem value="quick">{t("pages.ai_explorer.research_depth_quick")}</SelectItem>
            <SelectItem value="detailed">
              {t("pages.ai_explorer.research_depth_detailed")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label htmlFor="language">{t("pages.ai_explorer.report_language")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_language_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <LanguageSelect
          availableLanguages={researchPrefs.availableLanguages}
          selectedLanguage={researchPrefs.language}
          onAddLanguage={languageActions.addLanguage}
          onChangeLanguage={(lang) => languageActions.setLanguage(lang)}
          onDeleteLanguage={languageActions.deleteLanguage}
        />
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label htmlFor="report-format">{t("pages.ai_explorer.report_format")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_format_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select
          value={researchPrefs.reportFormatSource}
          onValueChange={(val) =>
            reportActions.setReportFormatSource(val as typeof researchPrefs.reportFormatSource)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder={t("pages.ai_explorer.report_format_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="structured_summary">
              {t("pages.ai_explorer.report_format_structured_summary")}
            </SelectItem>
            <SelectItem value="standard">{t("pages.ai_explorer.report_format_apa")}</SelectItem>
            <SelectItem value="file">{t("pages.ai_explorer.report_format_file")}</SelectItem>
            <SelectItem value="message">{t("pages.ai_explorer.report_format_custom")}</SelectItem>
          </SelectContent>
        </Select>
        {researchPrefs.reportFormatSource === "message" && (
          <Textarea
            className="min-h-20"
            id="report-format-text"
            placeholder={t("pages.ai_explorer.report_format_placeholder")}
            value={researchPrefs.reportFormatText}
            onChange={(e) => reportActions.setReportFormatText(e.target.value)}
          />
        )}
        {researchPrefs.reportFormatSource === "file" && (
          <div
            {...getRootProps()}
            className="flex flex-col gap-2 rounded border p-2"
            role="presentation"
            onClick={(e) => e.preventDefault()}
          >
            <input {...getInputProps()} />
            {researchPrefs.reportFormatFile ? (
              <div className="flex items-center justify-between">
                <p className="truncate text-sm">{researchPrefs.reportFormatFile.name}</p>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    reportActions.setReportFormatFile(null)
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault()
                  open()
                }}
              >
                <Upload className="mr-2 size-4" /> {t("pages.ai_explorer.upload_file")}
              </Button>
            )}
          </div>
        )}
      </div>

      <Accordion collapsible type="single">
        <AccordionItem value="advanced">
          <AccordionTrigger className="text-sm font-medium">
            {t("pages.ai_explorer.advanced_settings")}
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="space-y-2">
              <Tooltip>
                <TooltipTrigger className="w-full text-left">
                  <Label htmlFor="country-codes">{t("pages.ai_explorer.country_codes")}</Label>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]" side="right">
                  <p>{t("pages.ai_explorer.country_codes_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
              <Input
                id="country-codes"
                placeholder={t("pages.ai_explorer.country_codes_placeholder")}
                value={researchPrefs.countryCodes}
                onChange={(e) => configActions.setCountryCodes(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Tooltip>
                <TooltipTrigger className="w-full text-left">
                  <Label htmlFor="source-urls">{t("pages.ai_explorer.source_urls")}</Label>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]" side="right">
                  <p>{t("pages.ai_explorer.source_urls_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
              <div className="flex gap-2">
                <Input
                  className={newUrl && !isValidUrl(newUrl) ? "border-destructive" : ""}
                  placeholder={t("pages.ai_explorer.source_url_placeholder")}
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      addUrl()
                    }
                  }}
                />
                <Button
                  className="px-3"
                  disabled={!newUrl.trim() || !isValidUrl(newUrl)}
                  size="sm"
                  variant="outline"
                  onClick={addUrl}
                >
                  <Plus className="mr-1 size-3" />
                  {t("pages.ai_explorer.add")}
                </Button>
              </div>
              {newUrl && !isValidUrl(newUrl) && (
                <p className="text-destructive text-xs">{t("pages.ai_explorer.invalid_url")}</p>
              )}
              {currentUrls.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">
                    {t("pages.ai_explorer.added_urls", { count: currentUrls.length })}
                  </Label>
                  <UrlList urls={currentUrls} onRemove={removeUrl} />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <SourcesRequiredDialog
        open={showSourcesRequired}
        onClose={() => setShowSourcesRequired(false)}
      />
    </div>
  )
}
